import PropTypes from 'prop-types';

export const CardSkeleton = ({ count = 1 }) => {
    CardSkeleton.propTypes = {
        count: PropTypes.number,
    };

    return (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {Array(count)
                .fill(null)
                .map((_, index) => (
                    <div key={index} className="border rounded-lg p-4 animate-pulse">
                        <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
                        <div className="space-y-3">
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        </div>
                    </div>
                ))}
        </div>
    );
};

export const ListSkeleton = ({ lines = 5 }) => {
    ListSkeleton.propTypes = {
        lines: PropTypes.number,
    };

    return (
        <div className="space-y-3">
            {Array(lines)
                .fill(null)
                .map((_, index) => (
                    <div key={index} className="flex items-center space-x-4 animate-pulse">
                        <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                        <div className="flex-1 space-y-2">
                            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                    </div>
                ))}
        </div>
    );
};

export const TextBlockSkeleton = () => (
    <div className="space-y-4 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
        </div>
    </div>
);

export const ProfileSkeleton = () => (
    <div className="animate-pulse">
        <div className="flex items-center space-x-4 mb-6">
            <div className="h-16 w-16 bg-gray-200 rounded-full"></div>
            <div className="space-y-2">
                <div className="h-5 bg-gray-200 rounded w-48"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
            </div>
        </div>
        <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
        </div>
    </div>
);

export const StatsSkeleton = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array(4)
            .fill(null)
            .map((_, index) => (
                <div key={index} className="border rounded-lg p-4 animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                </div>
            ))}
    </div>
);

export const FormSkeleton = () => (
    <div className="space-y-6 animate-pulse">
        <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="h-10 bg-gray-200 rounded w-full"></div>
        </div>
        <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="h-10 bg-gray-200 rounded w-full"></div>
        </div>
        <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="h-32 bg-gray-200 rounded w-full"></div>
        </div>
        <div className="h-10 bg-gray-200 rounded w-1/4"></div>
    </div>
);

export const SimpleTableSkeleton = ({
    rows = 5,
    columns = 4,
    showHeader = true,
    density = 'normal',
}) => {
    SimpleTableSkeleton.propTypes = {
        rows: PropTypes.number,
        columns: PropTypes.number,
        showHeader: PropTypes.bool,
        density: PropTypes.oneOf(['compact', 'normal', 'relaxed']),
    };

    const densityClasses = {
        compact: 'py-2',
        normal: 'py-3',
        relaxed: 'py-4',
    };

    const renderSimpleHeader = () => (
        <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
                {Array(columns)
                    .fill(null)
                    .map((_, index) => (
                        <th key={`header-${index}`} className={`px-4 ${densityClasses[density]}`}>
                            <div
                                className="h-4 bg-gray-200 rounded animate-pulse"
                                style={{ width: index === 0 ? '60%' : '80%' }}
                            ></div>
                        </th>
                    ))}
            </tr>
        </thead>
    );

    const renderSimpleRow = rowIndex => (
        <tr className="border-b border-gray-100">
            {Array(columns)
                .fill(null)
                .map((_, colIndex) => (
                    <td
                        key={`cell-${rowIndex}-${colIndex}`}
                        className={`px-4 ${densityClasses[density]}`}
                    >
                        <div
                            className="h-4 bg-gray-200 rounded animate-pulse"
                            style={{
                                width: `${Math.floor(Math.random() * 40 + 40)}%`, // Random width between 40-80%
                            }}
                        ></div>
                    </td>
                ))}
        </tr>
    );

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full">
                {showHeader && renderSimpleHeader()}
                <tbody>
                    {Array(rows)
                        .fill(null)
                        .map((_, index) => renderSimpleRow(index))}
                </tbody>
            </table>
        </div>
    );
};

export const HoverableTableSkeleton = ({ rows = 5, columns = 4, showHeader = true }) => {
    HoverableTableSkeleton.propTypes = {
        rows: PropTypes.number,
        columns: PropTypes.number,
        showHeader: PropTypes.bool,
    };

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full">
                {showHeader && (
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            {Array(columns)
                                .fill(null)
                                .map((_, index) => (
                                    <th key={`header-${index}`} className="px-4 py-3 text-left">
                                        <div className="h-4 bg-gray-200 rounded animate-pulse w-4/5"></div>
                                    </th>
                                ))}
                        </tr>
                    </thead>
                )}
                <tbody>
                    {Array(rows)
                        .fill(null)
                        .map((_, rowIndex) => (
                            <tr
                                key={`row-${rowIndex}`}
                                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                            >
                                {Array(columns)
                                    .fill(null)
                                    .map((_, colIndex) => (
                                        <td
                                            key={`cell-${rowIndex}-${colIndex}`}
                                            className="px-4 py-3"
                                        >
                                            <div
                                                className="h-4 bg-gray-200 rounded animate-pulse"
                                                style={{ width: colIndex === 0 ? '70%' : '60%' }}
                                            ></div>
                                        </td>
                                    ))}
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
};

export const KanbanSkeleton = ({ columns = 3, cardsPerColumn = 4 }) => {
    KanbanSkeleton.propTypes = {
        columns: PropTypes.number,
        cardsPerColumn: PropTypes.number,
    };

    const renderCard = (columnIndex, cardIndex) => (
        <div
            key={`card-${columnIndex}-${cardIndex}`}
            className="bg-white p-3 rounded-lg shadow-sm mb-3 animate-pulse"
        >
            <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                <div className="flex items-center space-x-2 mt-3">
                    <div className="h-6 w-6 bg-gray-200 rounded-full"></div>
                    <div className="h-2 bg-gray-200 rounded w-16"></div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array(columns)
                .fill(null)
                .map((_, columnIndex) => (
                    <div key={`column-${columnIndex}`} className="bg-gray-50 rounded-lg p-4">
                        <div className="mb-4 animate-pulse">
                            <div className="h-5 bg-gray-200 rounded w-1/3"></div>
                        </div>
                        {Array(cardsPerColumn)
                            .fill(null)
                            .map((_, cardIndex) => renderCard(columnIndex, cardIndex))}
                    </div>
                ))}
        </div>
    );
};

export const InterviewScheduleSkeleton = () => {
    return (
        <div className="w-full p-4 rounded-[10px] bg-white mb-4 animate-pulse">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="h-6 bg-gray-200 rounded w-40"></div>
                <div className="h-5 bg-gray-200 rounded w-24"></div>
            </div>

            {/* Main Content */}
            <div className="flex lg:flex-row flex-col items-start gap-2">
                {/* Interview Lists Section */}
                <div className="lg:w-7/12 w-full">
                    {/* Search Bar */}
                    <div className="h-10 bg-gray-200 rounded-lg mb-4"></div>

                    {/* Interview Cards */}
                    <div className="space-y-3">
                        {[1, 2, 3].map((_, index) => (
                            <div key={index} className="border rounded-lg p-4">
                                <div className="flex justify-between mb-2">
                                    <div className="h-5 bg-gray-200 rounded w-32"></div>
                                    <div className="h-5 bg-gray-200 rounded w-24"></div>
                                </div>
                                <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
                                <div className="h-4 bg-gray-200 rounded w-36"></div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Calendar Section */}
                <div className="lg:w-5/12 w-full">
                    <div className="border rounded-lg p-4">
                        <div className="h-8 bg-gray-200 rounded w-full mb-4"></div>
                        <div className="grid grid-cols-7 gap-2">
                            {Array(35)
                                .fill(null)
                                .map((_, index) => (
                                    <div key={index} className="h-8 bg-gray-200 rounded"></div>
                                ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
